import { isTempClipId } from '@src/services/WriteQueueService';
import { useClipPreview } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { recordLastEditedClip } from '@src/utility/lastEditedClip';
import { LogManager } from '@src/utility/Logger';
import { useCallback } from 'react';

import type {
  ClipMetadata,
  OtherVideo,
  Story,
  VideoClip,
} from '../../types.ts/story';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { ClipPreviewStore } from '@src/stores-v2/ClipPreviewStore';

const logger = LogManager.getLogger('hook:useOpenClipPreview');

const getPublishedAspectRatio = (video: VideoClip) => {
  const videos = [video, ...(video.associatedVideos || [])];
  const published = videos.find(v => v.videoFilePrimary);
  if (published) return published.aspectRatio;
  return video.aspectRatio;
};

const isLightweightClipJson = (
  clipJson: unknown,
): clipJson is { isLightweightClip: boolean } => {
  return (
    typeof clipJson === 'object' &&
    clipJson !== null &&
    'isLightweightClip' in clipJson &&
    typeof (clipJson as { isLightweightClip: unknown }).isLightweightClip ===
      'boolean'
  );
};

export async function expandLightweightClipInBackground(
  clip: VideoClip,
  story: Story,
  videoCreator: IVideoCreatorStore,
  clipPreviewStore: ClipPreviewStore,
): Promise<void> {
  try {
    const cached = await clipPreviewStore.prefetchLightweight(
      clip,
      story,
      videoCreator,
    );

    const expandedVideo = cached.populatedClip as OtherVideo;
    if (!expandedVideo.videoSource) {
      logger.warn('Expansion completed but videoSource is still empty', {
        videoId: clip.id,
      });
      return;
    }

    // Update clip preview if still showing this clip.
    // If clip.id was a temp ID, replaceOptimisticVideoId may have swapped
    // videoClipPreview to a new object with a real ID during the await above.
    // In that case the exact ID check fails, so fall back to matching on
    // stable clipJson fields to confirm it's the same clip (not a different
    // clip the user opened after closing this modal).
    const currentPreview = videoCreator.videoClipPreview;
    const clipMeta = clip.clipJson;
    const previewMeta = currentPreview?.clipJson;
    const isOurPreview =
      currentPreview != null &&
      (currentPreview.id === clip.id ||
        isOurPreviewAfterIdSwap(clip, clipMeta, previewMeta));

    if (isOurPreview) {
      videoCreator.setClipPreviewAndUpdateSource({
        ...currentPreview,
        videoSource: expandedVideo.videoSource,
        transcriptionSnapshot: expandedVideo.transcriptionSnapshot,
        karaokeData: expandedVideo.karaokeData,
        scenes: expandedVideo.scenes,
        // The Section names the expansion seeded from the reel's sub-clips, so
        // a reel just saved opens with them rather than with its opening words
        // until it is reopened. Only when the preview has no reel of its own:
        // the rest of `clipJson` is the modal's to write, and by the time this
        // resolves it may have written some.
        ...(reelStructureToAdopt(previewMeta, expandedVideo.clipJson) ?? {}),
      } as typeof currentPreview);
    }

    logger.info('Lightweight clip expanded successfully', {
      videoId: clip.id,
      isOurPreview,
      currentPreviewId: currentPreview?.id,
    });
  } catch (error) {
    logger.error('Failed to expand lightweight clip', {
      videoId: clip.id,
      error: extractErrorInfo(error),
    });
  }
}

/**
 * The `clipJson` patch that carries the expansion's seeded reel onto the open
 * preview, or nothing when there is none to carry or the preview already has
 * a reel of its own.
 */
function reelStructureToAdopt(
  previewClipJson: ClipMetadata | undefined,
  expandedClipJson: ClipMetadata | undefined,
): { clipJson: ClipMetadata } | undefined {
  const reel = expandedClipJson?.reel;
  if (!reel || !previewClipJson || previewClipJson.reel) return undefined;
  return { clipJson: { ...previewClipJson, reel } };
}

function isOurPreviewAfterIdSwap(
  clip: VideoClip,
  clipMeta: ClipMetadata | undefined,
  previewMeta: ClipMetadata | undefined,
): boolean {
  return (
    isTempClipId(clip.id) &&
    clipMeta != null &&
    previewMeta != null &&
    clipMeta.sourceStoryId === previewMeta.sourceStoryId &&
    clipMeta.startTime === previewMeta.startTime &&
    clipMeta.duration === previewMeta.duration
  );
}

/**
 * Returns helpers to prefetch and open a clip in the preview/edit modal.
 *
 * - `prefetch(clip)` warms the cache (lightweight or persisted clip resources).
 *   Fire-and-forget; safe to call on hover.
 * - `open(clip, autoPlay?, dimensionOverride?)` sets
 *   `videoCreator.videoClipPreview`, which causes `SocialClipShareModal` to
 *   mount. `dimensionOverride` forces the initial selected aspect ratio (used
 *   when restoring a shared `?clipId=<variantId>` link to the exact AR variant).
 */
export function useOpenClipPreview() {
  const videoCreator = useVideoCreatorStore();
  const clipPreviewStore = useClipPreview();

  const prefetch = useCallback(
    (clip: OtherVideo) => {
      const currentStory = videoCreator.story;
      if (!clip.id || !currentStory?.id) return;
      if (isTempClipId(clip.id)) return;

      const hasPersistedResources =
        clip.resourcesMetadataJson?.videoSourceSnapshot != null;
      const isLightweight =
        isLightweightClipJson(clip.clipJson) &&
        clip.clipJson.isLightweightClip &&
        !hasPersistedResources;

      if (isLightweight) {
        clipPreviewStore.prefetchLightweight(clip, currentStory, videoCreator);
      } else {
        const videoRepository = videoCreator.datoClientStore.videoRepository;
        clipPreviewStore.prefetch(currentStory.id, clip, videoRepository);
      }
    },
    [videoCreator, clipPreviewStore],
  );

  const open = useCallback(
    async (clip: OtherVideo, autoPlay = false, dimensionOverride?: string) => {
      const currentStory = videoCreator.story;
      const hasPersistedResources =
        clip.resourcesMetadataJson?.videoSourceSnapshot != null;

      // Remember this as the story's last-edited clip (skip not-yet-persisted
      // temp ids, which won't match an otherVideos entry later).
      if (clip.id && !isTempClipId(clip.id)) {
        recordLastEditedClip(currentStory?.id, clip.id);
      }

      if (clip.id) {
        const enriched = clipPreviewStore.enrichClipFromCache(clip, clip.id);
        if (enriched) {
          const selectedDimension =
            dimensionOverride ??
            (videoCreator.lastClipDimension.get(clip.id) ||
              getPublishedAspectRatio(clip));

          videoCreator.setClipPreviewAndUpdateSource({
            ...enriched,
            ...(selectedDimension ? { selectedDimension } : {}),
            autoPlay,
          });
          return;
        }
      }

      const isSaving = isTempClipId(clip.id);
      const isLightweight =
        isSaving ||
        (isLightweightClipJson(clip.clipJson) &&
          clip.clipJson.isLightweightClip &&
          !hasPersistedResources);

      if (isLightweight) {
        videoCreator.setClipPreviewAndUpdateSource({
          ...clip,
          selectedDimension: dimensionOverride ?? getPublishedAspectRatio(clip),
          autoPlay,
        });

        if (clip.id && currentStory) {
          expandLightweightClipInBackground(
            clip,
            currentStory,
            videoCreator,
            clipPreviewStore,
          );
        }
        return;
      }

      const selectedDimension =
        dimensionOverride ??
        ((clip.id ? videoCreator.lastClipDimension.get(clip.id) : null) ||
          getPublishedAspectRatio(clip));

      videoCreator.setClipPreviewAndUpdateSource({
        ...clip,
        ...(selectedDimension ? { selectedDimension } : {}),
        autoPlay,
      });
    },
    [videoCreator, clipPreviewStore],
  );

  return { prefetch, open };
}
